// frontend/src/pages/technician/TechnicianReports.js

import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Button,
    Grid,
    Paper,
    Alert,
    CircularProgress,
    TextField,
    Tab,
    Tabs,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Chip,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    IconButton
} from '@mui/material';
import {
    Assessment,
    Add,
    Edit,
    Save,
    Schedule,
    Assignment,
    Build,
    TrendingUp,
    CalendarToday,
    CheckCircle,
    Warning,
    Refresh
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { es } from 'date-fns/locale';
import { technicianApiService } from '../../services/technicianApiService';

const TechnicianReports = () => {
    const [currentTab, setCurrentTab] = useState(0);
    const [reports, setReports] = useState([]);
    const [workStats, setWorkStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [reportDialog, setReportDialog] = useState(false);
    const [editingReport, setEditingReport] = useState(null);
    const [error, setError] = useState(null);
    const [dateRange, setDateRange] = useState({
        start: new Date(new Date().setDate(new Date().getDate() - 30)),
        end: new Date()
    });

    const [reportForm, setReportForm] = useState({
        report_date: new Date().toISOString().split('T')[0],
        installations_completed: 0,
        tickets_resolved: 0,
        total_work_hours: 0,
        issues_encountered: '',
        summary: ''
    });

    useEffect(() => {
        loadReports();
        loadWorkStats();
    }, [dateRange]);

    const loadReports = async () => {
        try {
            setLoading(true);
            setError(null);
            const startDate = dateRange.start ? dateRange.start.toISOString().split('T')[0] : null;
            const endDate = dateRange.end ? dateRange.end.toISOString().split('T')[0] : null;

            const data = await technicianApiService.getDailyReports(startDate, endDate);
            setReports(data.reports || []);
        } catch (error) {
            console.error('Error cargando reportes:', error);
            setError(error.response?.data?.message || 'Error cargando reportes');
        } finally {
            setLoading(false);
        }
    };

    const loadWorkStats = async () => {
        try {
            const stats = await technicianApiService.getWorkSummary('month');
            setWorkStats(stats);
        } catch (error) {
            console.error('Error cargando estadísticas:', error);
            // No mostramos error aquí para que no interfiera con la carga de reportes
        }
    };

    const openReportDialog = (report = null) => {
        if (report) {
            setEditingReport(report);
            setReportForm({
                report_date: report.report_date,
                installations_completed: report.installations_completed || 0,
                tickets_resolved: report.tickets_resolved || 0,
                total_work_hours: report.total_work_hours || 0,
                issues_encountered: report.issues_encountered || '',
                summary: report.summary || ''
            });
        } else {
            setEditingReport(null);
            setReportForm({
                report_date: new Date().toISOString().split('T')[0],
                installations_completed: 0,
                tickets_resolved: 0,
                total_work_hours: 0,
                issues_encountered: '',
                summary: ''
            });
        }
        setReportDialog(true);
    };

    const handleFormChange = (field, value) => {
        setReportForm(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const saveReport = async () => {
        try {
            await technicianApiService.createDailyReport(reportForm);
            setReportDialog(false);
            await loadReports();
            await loadWorkStats();
            setError(null);
        } catch (error) {
            console.error('Error guardando reporte:', error);
            setError(error.response?.data?.message || 'Error guardando reporte');
        }
    };

    const getReportStatusColor = (report) => {
        if ((report.installations_completed || 0) === 0 && (report.tickets_resolved || 0) === 0) {
            return 'default';
        } else if ((report.total_work_hours || 0) >= 8) {
            return 'success';
        } else {
            return 'warning';
        }
    };

    const getReportStatusText = (report) => {
        if ((report.installations_completed || 0) === 0 && (report.tickets_resolved || 0) === 0) {
            return 'Sin actividad';
        } else if ((report.total_work_hours || 0) >= 8) {
            return 'Jornada completa';
        } else {
            return 'Jornada parcial';
        }
    };

    const refreshData = async () => {
        await Promise.all([loadReports(), loadWorkStats()]);
    };

    if (loading && reports.length === 0) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            {/* Header */}
            <Paper sx={{ p: 2, mb: 3 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box>
                        <Typography variant="h5" gutterBottom>
                            Reportes de Trabajo
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            Gestiona tus reportes diarios y revisa tu rendimiento
                        </Typography>
                    </Box>
                    <Box display="flex" gap={1}>
                        <Button
                            variant="contained"
                            startIcon={<Add />}
                            onClick={() => openReportDialog()}
                        >
                            Nuevo Reporte
                        </Button>
                        <IconButton onClick={refreshData}>
                            <Refresh />
                        </IconButton>
                    </Box>
                </Box>
            </Paper>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {/* Tabs */}
            <Paper sx={{ mb: 3 }}>
                <Tabs value={currentTab} onChange={(e, newValue) => setCurrentTab(newValue)}>
                    <Tab label="Reportes Diarios" icon={<CalendarToday />} />
                    <Tab label="Estadísticas" icon={<TrendingUp />} />
                </Tabs>
            </Paper>

            {/* Tab 0: Reportes Diarios */}
            {currentTab === 0 && (
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        {/* Filtros de fecha */}
                        <Paper sx={{ p: 2, mb: 2 }}>
                            <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                                <Typography variant="subtitle1">Período:</Typography>
                                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
                                    <DatePicker
                                        label="Desde"
                                        value={dateRange.start}
                                        onChange={(date) => setDateRange(prev => ({ ...prev, start: date }))}
                                        slotProps={{
                                            textField: {
                                                size: 'small',
                                                sx: { minWidth: 150 }
                                            }
                                        }}
                                    />
                                    <DatePicker
                                        label="Hasta"
                                        value={dateRange.end}
                                        onChange={(date) => setDateRange(prev => ({ ...prev, end: date }))}
                                        slotProps={{
                                            textField: {
                                                size: 'small',
                                                sx: { minWidth: 150 }
                                            }
                                        }}
                                    />
                                </LocalizationProvider>
                            </Box>
                        </Paper>

                        {/* Lista de reportes */}
                        {reports.length > 0 ? (
                            <TableContainer component={Paper}>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Fecha</TableCell>
                                            <TableCell align="center">Instalaciones</TableCell>
                                            <TableCell align="center">Tickets</TableCell>
                                            <TableCell align="center">Horas</TableCell>
                                            <TableCell align="center">Estado</TableCell>
                                            <TableCell align="center">Acciones</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {reports.map((report) => (
                                            <TableRow key={report.id || report.report_date}>
                                                <TableCell>
                                                    <Typography variant="subtitle2">
                                                        {new Date(report.report_date).toLocaleDateString('es-PE', {
                                                            weekday: 'long',
                                                            year: 'numeric',
                                                            month: 'long',
                                                            day: 'numeric'
                                                        })}
                                                    </Typography>
                                                    {report.summary && (
                                                        <Typography variant="caption" color="textSecondary">
                                                            {report.summary.substring(0, 50)}...
                                                        </Typography>
                                                    )}
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Chip
                                                        label={report.installations_completed || 0}
                                                        color={(report.installations_completed || 0) > 0 ? 'success' : 'default'}
                                                        size="small"
                                                    />
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Chip
                                                        label={report.tickets_resolved || 0}
                                                        color={(report.tickets_resolved || 0) > 0 ? 'info' : 'default'}
                                                        size="small"
                                                    />
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Typography variant="body2">
                                                        {report.total_work_hours || 0}h
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Chip
                                                        label={getReportStatusText(report)}
                                                        color={getReportStatusColor(report)}
                                                        size="small"
                                                    />
                                                </TableCell>
                                                <TableCell align="center">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => openReportDialog(report)}
                                                    >
                                                        <Edit />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        ) : (
                            <Alert severity="info">
                                No hay reportes en el período seleccionado
                            </Alert>
                        )}
                    </Grid>
                </Grid>
            )}

            {/* Tab 1: Estadísticas */}
            {currentTab === 1 && (
                <Grid container spacing={3}>
                    {/* Resumen del mes */}
                    <Grid item xs={12}>
                        <Typography variant="h6" gutterBottom>
                            Resumen del Mes
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6} md={3}>
                                <Card>
                                    <CardContent>
                                        <Box display="flex" alignItems="center">
                                            <Assignment color="primary" sx={{ mr: 2 }} />
                                            <Box>
                                                <Typography color="textSecondary" gutterBottom>
                                                    Instalaciones
                                                </Typography>
                                                <Typography variant="h4">
                                                    {workStats?.total_installations || 0}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>

                            <Grid item xs={12} sm={6} md={3}>
                                <Card>
                                    <CardContent>
                                        <Box display="flex" alignItems="center">
                                            <Build color="info" sx={{ mr: 2 }} />
                                            <Box>
                                                <Typography color="textSecondary" gutterBottom>
                                                    Tickets Resueltos
                                                </Typography>
                                                <Typography variant="h4">
                                                    {workStats?.total_tickets || 0}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>

                            <Grid item xs={12} sm={6} md={3}>
                                <Card>
                                    <CardContent>
                                        <Box display="flex" alignItems="center">
                                            <Schedule color="success" sx={{ mr: 2 }} />
                                            <Box>
                                                <Typography color="textSecondary" gutterBottom>
                                                    Horas Trabajadas
                                                </Typography>
                                                <Typography variant="h4">
                                                    {workStats?.total_hours || 0}h
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>

                            <Grid item xs={12} sm={6} md={3}>
                                <Card>
                                    <CardContent>
                                        <Box display="flex" alignItems="center">
                                            <TrendingUp color="warning" sx={{ mr: 2 }} />
                                            <Box>
                                                <Typography color="textSecondary" gutterBottom>
                                                    Promedio Diario
                                                </Typography>
                                                <Typography variant="h4">
                                                    {workStats?.daily_average || 0}h
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>
                    </Grid>

                    {/* Rendimiento */}
                    <Grid item xs={12} md={6}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    Indicadores de Rendimiento
                                </Typography>
                                <List>
                                    <ListItem>
                                        <ListItemIcon>
                                            <CheckCircle color="success" />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary="Eficiencia en Instalaciones"
                                            secondary={`${workStats?.installation_efficiency || 0}% de instalaciones completadas exitosamente`}
                                        />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemIcon>
                                            <Assessment color="info" />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary="Tiempo Promedio por Instalación"
                                            secondary={`${workStats?.avg_installation_time || 0} minutos`}
                                        />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemIcon>
                                            <Build color="primary" />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary="Tiempo Promedio de Resolución"
                                            secondary={`${workStats?.avg_resolution_time || 0} minutos por ticket`}
                                        />
                                    </ListItem>
                                </List>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Metas y objetivos */}
                    <Grid item xs={12} md={6}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    Metas del Mes
                                </Typography>
                                <List>
                                    <ListItem>
                                        <ListItemIcon>
                                            <Assignment color={workStats?.installation_goal_reached ? 'success' : 'warning'} />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary="Meta de Instalaciones"
                                            secondary={`${workStats?.total_installations || 0} / ${workStats?.installation_goal || 20} instalaciones`}
                                        />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemIcon>
                                            <Schedule color={workStats?.hours_goal_reached ? 'success' : 'warning'} />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary="Meta de Horas"
                                            secondary={`${workStats?.total_hours || 0} / ${workStats?.hours_goal || 160} horas`}
                                        />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemIcon>
                                            <Build color={workStats?.tickets_goal_reached ? 'success' : 'warning'} />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary="Meta de Tickets"
                                            secondary={`${workStats?.total_tickets || 0} / ${workStats?.tickets_goal || 50} tickets`}
                                        />
                                    </ListItem>
                                </List>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            )}

            {/* Dialog para crear/editar reporte */}
            <Dialog open={reportDialog} onClose={() => setReportDialog(false)} maxWidth="md" fullWidth>
                <DialogTitle>
                    {editingReport ? 'Editar Reporte' : 'Nuevo Reporte Diario'}
                </DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Fecha del reporte"
                                type="date"
                                value={reportForm.report_date}
                                onChange={(e) => handleFormChange('report_date', e.target.value)}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Horas trabajadas"
                                type="number"
                                value={reportForm.total_work_hours}
                                onChange={(e) => handleFormChange('total_work_hours', parseFloat(e.target.value) || 0)}
                                inputProps={{ min: 0, max: 24, step: 0.5 }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Instalaciones completadas"
                                type="number"
                                value={reportForm.installations_completed}
                                onChange={(e) => handleFormChange('installations_completed', parseInt(e.target.value) || 0)}
                                inputProps={{ min: 0 }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Tickets resueltos"
                                type="number"
                                value={reportForm.tickets_resolved}
                                onChange={(e) => handleFormChange('tickets_resolved', parseInt(e.target.value) || 0)}
                                inputProps={{ min: 0 }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                multiline
                                rows={3}
                                label="Problemas encontrados"
                                value={reportForm.issues_encountered}
                                onChange={(e) => handleFormChange('issues_encountered', e.target.value)}
                                placeholder="Describe cualquier problema, dificultad o inconveniente encontrado durante el día"
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                multiline
                                rows={3}
                                label="Resumen del día"
                                value={reportForm.summary}
                                onChange={(e) => handleFormChange('summary', e.target.value)}
                                placeholder="Resumen general de las actividades realizadas durante el día"
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setReportDialog(false)}>
                        Cancelar
                    </Button>
                    <Button
                        onClick={saveReport}
                        variant="contained"
                        startIcon={<Save />}
                    >
                        {editingReport ? 'Actualizar' : 'Guardar'} Reporte
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default TechnicianReports;